import React, {useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {createFragmentContainer, graphql} from 'react-relay';
import {SubNavigation} from 'web-components';
import {useIntl} from 'react-intl';
import {PageWrapper, StandardMargin} from './UtilizationReport.styled';
import {HeaderBar} from './HeaderBar';
import UtilizationReportTotals, {UtilizationReportTotalsQuery} from './UtilizationReportTotals';
import ForecastQueryRenderer from '../../../ForecastQueryRenderer';
import {filtersToSearchQuery} from './UtilizationReportUtils';
import Util from '../../../forecast-app/shared/util/util';
import * as tracking from '../../../tracking';
import {useGenericReportContext} from '../GenericReportContext';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {getPeriodEyeOptions} from './Period';
import {getInitialOptionsFromCheckedOptions} from '../../../the_eye_util';
import moment from 'moment';

export const UTILIZATION_PAGES = {
	PERIOD: 'period',
	MONTH_BY_MONTH: 'month-by-month',
};

export const UTILIZATION_DROPDOWN = {
	BILLABLE_UTILIZATION: 'forecastBillableUtilization',
	RESOURCE_UTILIZATION: 'forecastResourceUtilization',
};

export const GROUP_BY = {
	NONE: 'none',
	ROLE: 'role',
	DEPARTMENT: 'department',
};

export const UtilizationReport = ({viewer}) => {
	const history = useHistory();
	const {pathname} = useLocation();
	const intl = useIntl();
	const getActivePage = () => {
		const pathIndex = pathname.lastIndexOf('/');
		return pathname.substr(pathIndex + 1);
	};

	const {
		getTopReportHeader,
		getBottomReportHeader,
		reportName,
		reportOwner,
		startDate,
		endDate,
		periodDate,
		theEyeOptionsChecked,
		filterValue,
		firstDropdownValue,
		secondDropdownValue,
		handleSetStartDate,
		handleSetEndDate,
		handleSetPeriodDate,
		handleSetFirstDropdownValue,
		handleSetSecondDropdownValue,
	} = useGenericReportContext();

	const defaultOptions = getPeriodEyeOptions(viewer.company.isUsingProjectAllocation, viewer.company.isUsingSchedulingPlanMode);

	const theEyeOptionsNestedTree = getInitialOptionsFromCheckedOptions(defaultOptions, theEyeOptionsChecked);

	// VARIABLES AND CONSTANTS ------------------------------------------------
	const UTILIZATION_PAGES_NAVIGATION_OPTIONS = [
		{
			value: 'period',
			label: intl.formatMessage({id: 'common.period'}),
		},
		{
			value: 'month-by-month',
			label: intl.formatMessage({id: 'common.month_by_month'}),
		},
	];

	const getStartDateAndEndDateByActivePage = () => {
		const activePage = getActivePage();
		if (activePage === UTILIZATION_PAGES.PERIOD) {
			return {
				startDate: startDate,
				endDate: endDate,
			};
		} else {
			return {
				startDate: moment.utc({year: periodDate, month: 0, date: 1}),
				endDate: moment.utc({year: periodDate, month: 11, date: 31}),
			};
		}
	};

	// STATE ------------------------------------------------------------------
	const selectedDates = getStartDateAndEndDateByActivePage();
	const selectedYear = periodDate;
	const showResourceUtilization = firstDropdownValue === UTILIZATION_DROPDOWN.RESOURCE_UTILIZATION;
	const groupBy = secondDropdownValue || GROUP_BY.NONE;

	// HANDLERS ---------------------------------------------------------------
	const handleSetActivePage = value => {
		if (getActivePage() !== value) {
			history.push(value);
		}
	};

	const handleChangeDates = (selectedStartDate, selectedEndDate) => {
		tracking.trackElementClicked('Date selected', {startDate: Util.GetYearMonthDateFromMomentDate(selectedStartDate), endDate: Util.GetYearMonthDateFromMomentDate(selectedEndDate), dateRange: selectedEndDate.diff(selectedStartDate, 'days') + 1});
		handleSetStartDate(selectedStartDate);
		handleSetEndDate(selectedEndDate);
	};

	const handleChangeSelectedYear = selectedYear => {
		handleSetPeriodDate(selectedYear);
	};

	useEffect(() => {
		tracking.trackPage('Utilisation Report_NEW', null, null, {
			'Tab name': getActivePage(),
		});
	}, [pathname]);

	let csvDownloadFunction = null;
	const handleCsvDownload = () => {
		tracking.trackElementClicked('CSV download');
		csvDownloadFunction && csvDownloadFunction();
	};
	const setCsvDownloadFunction = downloadFunction => (csvDownloadFunction = downloadFunction);

	const getTitleHeaderBar = () => {
		const content = [];
		const actualPersonId = viewer.actualPersonId;
		const ownedReport = reportOwner && ((reportOwner.active && reportOwner.id === actualPersonId) || !reportOwner.active);
		const reportNameProps = {
			defaultTitle: intl.formatMessage({id: 'utilization.utilization_report'}) + ', ' + intl.formatMessage({id: 'common.untitled'}),
			placeholder: intl.formatMessage({id: 'time_reg_report.report_name'}),
		};
		if (ownedReport) {
			content.push(...getTopReportHeader(reportNameProps));
		}

		content.push({
			type: TopHeaderBar.TYPE.FEEDBACK,
			userEmail: viewer.email,
			link: 'https://experience.forecast.it/feedback-utilization-report',
		});

		const onboardingFlows = [
			{
				id: 'Utilization Report - Overview',
				title: 'Overview',
				description: null,
				contentId: '1615119474bItz0383',
			},
		];
		if (getActivePage() === 'period') {
			onboardingFlows.push({id: 'Utilization Report - Period', title: 'Period View', description: null, contentId: '1615199238qDiv893'});
		}
		if (getActivePage() === 'month-by-month') {
			onboardingFlows.push({id: 'Utilization Report Month by Month', title: 'Month by Month View', description: null, contentId: '1615199848xEgx5442'});
		}
		content.push({
			id: 'onboarding-component',
			intl: intl,
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: 'Utilization Report',
			options: onboardingFlows,
			helpCenterLink: 'https://help.forecast.it/utilization-report',
		});

		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={ownedReport ? null : reportName} content={content} />
			</TopHeaderBarWrapper>
		);
	};

	const getHeaderBar = () => {
		return (
			<HeaderBar
				getBottomReportHeader={getBottomReportHeader}
				viewer={viewer}
				activePage={getActivePage()}
				onChangeDates={handleChangeDates}
				selectedDates={selectedDates}
				selectedYear={selectedYear}
				onChangeYear={handleChangeSelectedYear}
				utilizationPages={UTILIZATION_PAGES}
				showResourceUtilization={showResourceUtilization}
				groupBy={groupBy}
				handleSetFirstDropdownValue={handleSetFirstDropdownValue}
				handleSetSecondDropdownValue={handleSetSecondDropdownValue}
				handleCsvDownload={handleCsvDownload}
			/>
		);
	};

	const selectedStartDate = selectedDates.startDate;
	const selectedEndDate = selectedDates.endDate;

	return (
		<PageWrapper>
			{getTitleHeaderBar()}

			<div style={{marginTop: '16px'}}>
				<StandardMargin>
					<SubNavigation>
						{UTILIZATION_PAGES_NAVIGATION_OPTIONS.map(option => (
							<SubNavigation.NavigationItem active={getActivePage() === option.value} onClick={() => handleSetActivePage(option.value)} key={option.value} userpilot={'utilization-' + option.value + '-tab'}>
								{option.label}
							</SubNavigation.NavigationItem>
						))}
					</SubNavigation>
				</StandardMargin>
			</div>
			<StandardMargin>{getHeaderBar()}</StandardMargin>
			<ForecastQueryRenderer
				query={UtilizationReportTotalsQuery}
				componentName={'utilization_report_totals'}
				showLoader={false}
				variables={{
					searchQuery: filtersToSearchQuery(filterValue, [
						{key: 'startDate', value: selectedStartDate.format('YYYY-MM-DD')},
						{key: 'endDate', value: selectedEndDate.format('YYYY-MM-DD')},
					]),
					startYear: selectedStartDate.year(),
					startMonth: selectedStartDate.month() + 1,
					startDay: selectedStartDate.date(),
					endYear: selectedEndDate.year(),
					endMonth: selectedEndDate.month() + 1,
					endDay: selectedEndDate.date(),
				}}
				render={relayProps => {
					return (
						<UtilizationReportTotals
							theEyeOptionsNestedTree={theEyeOptionsNestedTree}
							startDate={selectedStartDate}
							endDate={selectedEndDate}
							filters={filterValue}
							showResourceUtilization={showResourceUtilization}
							setCsvDownloadFunction={setCsvDownloadFunction}
							intl={intl}
							availableFeatureFlags={viewer.availableFeatureFlags}
							groupBy={groupBy}
							{...relayProps}
						/>
					);
				}}
			/>
		</PageWrapper>
	);
};

const UtilizationReportQuery = graphql`
	query UtilizationReport_Query {
		viewer {
			actualPersonId
			component(name: "utilization_report")
			...UtilizationReport_viewer
		}
	}
`;

export {UtilizationReportQuery};

export default createFragmentContainer(UtilizationReport, {
	viewer: graphql`
		fragment UtilizationReport_viewer on Viewer {
			id
			actualPersonId
			email
			availableFeatureFlags {
				id
				key
			}
			company {
				isUsingProjectAllocation
				isUsingSchedulingPlanMode
				modules {
					moduleType
				}
			}
			filters(first: 1000000, filterSection: UTILIZATION_REPORT) @connection(key: "Viewer_filters", filters: []) {
				edges {
					node {
						id
						name
						section
						value
						updatedAt
					}
				}
			}
		}
	`,
});
